import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Calculator, Loader2, Check, AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Regularizar costos — herramienta administrativa minimal.
 *
 * Permite seleccionar un mes y, opcionalmente, un técnico. Lista las
 * reparaciones finalizadas en ese mes con su desglose financiero actual.
 * El admin puede completar/corregir, para cada orden, los costos de
 * repuestos, el porcentaje operativo y el porcentaje técnico, y aplicar
 * la regularización. Reutiliza el motor financiero existente.
 *
 * Preserva: production_snapshot, finalized_at, technician_id, items,
 * cobros, metas. Idempotente: segunda aplicación preserva el breakdown
 * previo original.
 */
function monthToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AdminRegularize() {
  const [month, setMonth] = useState(monthToday());
  const [technicianId, setTechnicianId] = useState("");
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [opPctSuggested, setOpPctSuggested] = useState(35);
  const [reason, setReason] = useState("");
  const [applying, setApplying] = useState(false);

  // Form state por fila: parts_policy / parts_cost / operational_pct / technician_pct / selected
  const [edits, setEdits] = useState({}); // { [order_id]: {...} }

  useEffect(() => {
    api.get("/users?role=technician&active=true")
      .then((r) => setTechnicians(r.data || []))
      .catch(() => {});
  }, []);

  async function load() {
    if (!month) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (technicianId) params.set("technician_id", technicianId);
      const r = await api.get(`/admin/finance/regularize/preview?${params.toString()}`);
      setRows(r.data.rows || []);
      setOpPctSuggested(r.data.workshop_operational_pct || 35);
      // Reset edits — valor inicial por fila basado en lo que ya haya
      const initial = {};
      (r.data.rows || []).forEach((x) => {
        const bd = x.current_breakdown || {};
        initial[x.order_id] = {
          selected: false,
          parts_policy: bd.parts_policy === "WITH_PARTS" ? "WITH_PARTS" : "NO_PARTS",
          parts_cost: bd.parts_cost ?? 0,
          operational_pct: bd.operational_pct_applied ?? (r.data.workshop_operational_pct || 35),
          technician_pct: bd.technician_pct_applied ?? x.technician_commission_rate ?? "",
        };
      });
      setEdits(initial);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  // Carga automática al cambiar filtros
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, technicianId]);

  function setEdit(order_id, patch) {
    setEdits((prev) => ({ ...prev, [order_id]: { ...(prev[order_id] || {}), ...patch } }));
  }

  const selectedIds = useMemo(
    () => Object.entries(edits).filter(([, v]) => v.selected).map(([k]) => k),
    [edits],
  );

  function selectAllMissing() {
    const next = { ...edits };
    rows.forEach((r) => {
      if (!r.already_regularized && (!r.current_breakdown.status || r.current_breakdown.status !== "OK")) {
        next[r.order_id] = { ...(next[r.order_id] || {}), selected: true };
      }
    });
    setEdits(next);
  }
  function selectNone() {
    const next = { ...edits };
    Object.keys(next).forEach((k) => { next[k] = { ...next[k], selected: false }; });
    setEdits(next);
  }

  async function applySelected() {
    if (selectedIds.length === 0) return toast.error("Selecciona al menos una orden.");
    if ((reason || "").trim().length < 3) return toast.error("Ingresá un motivo (mín. 3 caracteres).");
    const entries = selectedIds.map((oid) => {
      const e = edits[oid];
      const policy = e.parts_policy === "WITH_PARTS" ? "WITH_PARTS" : "NO_PARTS";
      const payload = { order_id: oid, parts_policy: policy };
      if (policy === "WITH_PARTS") payload.parts_cost = Number(e.parts_cost) || 0;
      if (e.operational_pct !== "" && e.operational_pct != null) payload.operational_pct = Number(e.operational_pct);
      if (e.technician_pct !== "" && e.technician_pct != null) payload.technician_pct = Number(e.technician_pct);
      return payload;
    });
    const confirmMsg =
      `Aplicar regularización a ${entries.length} ${entries.length === 1 ? "orden" : "órdenes"}.\n\n` +
      `Esto NO altera producción, fechas, técnico ni cobros.\n` +
      `El cálculo reutilizará el motor financiero vigente.`;
    if (!window.confirm(confirmMsg)) return;
    setApplying(true);
    try {
      const r = await api.post("/admin/finance/regularize/apply", {
        reason: reason.trim(),
        entries,
      });
      const ok = r.data.count || 0;
      toast.success(`Regularización aplicada: ${ok} ${ok === 1 ? "orden" : "órdenes"}`);
      await load();
      setReason("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  }

  const kpis = useMemo(() => {
    let n = 0, nReg = 0, nPending = 0, sale = 0;
    rows.forEach((r) => {
      n += 1;
      sale += r.final_price || 0;
      if (r.already_regularized) nReg += 1;
      else if (!r.current_breakdown || r.current_breakdown.status !== "OK") nPending += 1;
    });
    return { n, nReg, nPending, sale: Math.round(sale * 100) / 100 };
  }, [rows]);

  return (
    <div className="p-5 max-w-7xl mx-auto space-y-4" data-testid="admin-regularize-page">
      <div className="flex items-center gap-2">
        <Calculator size={22} className="text-emerald-700" />
        <h1 className="font-heading font-extrabold text-2xl text-slate-900">Regularizar costos</h1>
      </div>
      <p className="text-sm text-slate-600 max-w-3xl">
        Herramienta administrativa para completar/corregir costos de reparaciones finalizadas sin alterar
        producción, fechas, técnico ni cobros. Reutiliza el motor financiero existente: con repuestos
        descuenta el costo real; sin repuestos aplica el porcentaje operativo del taller; redondeo hacia arriba.
      </p>

      <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Mes</label>
          <input
            type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="input-base" data-testid="regularize-month"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Técnico</label>
          <select
            value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}
            className="input-base min-w-[220px]" data-testid="regularize-tech"
          >
            <option value="">Todos los técnicos</option>
            {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <button
          type="button" onClick={load} disabled={loading}
          className="btn-secondary inline-flex items-center gap-1"
          data-testid="regularize-reload"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Recargar
        </button>
        <div className="ml-auto flex items-center gap-3 text-xs">
          <span className="font-mono text-slate-500">Total {kpis.n}</span>
          <span className="font-mono text-emerald-700">Regularizadas {kpis.nReg}</span>
          <span className="font-mono text-amber-700">Pendientes {kpis.nPending}</span>
          <span className="font-mono text-slate-700">Prod. ${kpis.sale.toFixed(2)}</span>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center gap-2 p-2 border-b bg-slate-50 text-xs">
          <button onClick={selectAllMissing} className="px-2 py-1 rounded hover:bg-slate-200 font-mono" data-testid="regularize-select-pending">Seleccionar pendientes</button>
          <button onClick={selectNone} className="px-2 py-1 rounded hover:bg-slate-200 font-mono">Ninguno</button>
          <span className="ml-auto font-mono text-slate-500">Op% sugerido: {opPctSuggested}%</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-xs uppercase font-mono text-slate-600">
              <tr>
                <th className="p-2 text-left w-8"></th>
                <th className="p-2 text-left">Orden</th>
                <th className="p-2 text-left">Equipo</th>
                <th className="p-2 text-left">Técnico</th>
                <th className="p-2 text-right">Final $</th>
                <th className="p-2 text-left">Clasif.</th>
                <th className="p-2 text-right">Costo repuestos</th>
                <th className="p-2 text-right">Op %</th>
                <th className="p-2 text-right">Téc %</th>
                <th className="p-2 text-right">Costo total</th>
                <th className="p-2 text-right">Ganancia</th>
                <th className="p-2 text-right">Comisión</th>
                <th className="p-2 text-left">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={13} className="p-6 text-center text-slate-500">Sin reparaciones finalizadas en el mes seleccionado.</td></tr>
              )}
              {rows.map((r) => {
                const e = edits[r.order_id] || {};
                const policy = e.parts_policy || "NO_PARTS";
                const fp = r.final_price || 0;
                const parts = policy === "WITH_PARTS" ? (Number(e.parts_cost) || 0) : 0;
                const op = Number(e.operational_pct ?? opPctSuggested) || 0;
                const opCost = policy === "WITH_PARTS" ? 0 : Math.ceil(fp * op / 100);
                const cost = parts + opCost;
                const base = Math.max(0, fp - cost);
                const techPct = e.technician_pct === "" || e.technician_pct == null ? null : Number(e.technician_pct);
                const comm = techPct == null ? null : Math.ceil(base * techPct / 100);
                const profit = Math.round((fp - cost) * 100) / 100;
                return (
                  <tr key={r.order_id} className={e.selected ? "bg-emerald-50" : ""} data-testid={`regularize-row-${r.order_number}`}>
                    <td className="p-2">
                      <input
                        type="checkbox" checked={!!e.selected}
                        onChange={(ev) => setEdit(r.order_id, { selected: ev.target.checked })}
                        data-testid={`regularize-check-${r.order_number}`}
                      />
                    </td>
                    <td className="p-2 font-mono text-xs">{r.order_number}</td>
                    <td className="p-2 text-xs truncate max-w-[180px]" title={r.equipment}>{r.equipment || "—"}</td>
                    <td className="p-2 text-xs truncate max-w-[140px]">{r.technician_name || "—"}</td>
                    <td className="p-2 text-right font-mono">${fp.toFixed(2)}</td>
                    <td className="p-2">
                      <select
                        value={policy} onChange={(ev) => setEdit(r.order_id, { parts_policy: ev.target.value })}
                        className="text-xs px-1 py-0.5 border border-slate-300 rounded"
                        data-testid={`regularize-policy-${r.order_number}`}
                      >
                        <option value="NO_PARTS">Sin repuestos</option>
                        <option value="WITH_PARTS">Con repuestos</option>
                      </select>
                    </td>
                    <td className="p-2 text-right">
                      <input
                        type="number" step="0.01" min="0"
                        value={policy === "WITH_PARTS" ? e.parts_cost ?? 0 : ""}
                        onChange={(ev) => setEdit(r.order_id, { parts_cost: ev.target.value })}
                        disabled={policy !== "WITH_PARTS"}
                        className="w-24 px-1 py-0.5 border border-slate-300 rounded font-mono text-right text-xs disabled:bg-slate-100"
                        data-testid={`regularize-parts-${r.order_number}`}
                      />
                    </td>
                    <td className="p-2 text-right">
                      <input
                        type="number" step="0.1" min="0" max="100"
                        value={e.operational_pct ?? ""}
                        onChange={(ev) => setEdit(r.order_id, { operational_pct: ev.target.value })}
                        disabled={policy === "WITH_PARTS"}
                        className="w-16 px-1 py-0.5 border border-slate-300 rounded font-mono text-right text-xs disabled:bg-slate-100"
                        data-testid={`regularize-op-${r.order_number}`}
                      />
                    </td>
                    <td className="p-2 text-right">
                      <input
                        type="number" step="0.1" min="0" max="100"
                        value={e.technician_pct ?? ""}
                        onChange={(ev) => setEdit(r.order_id, { technician_pct: ev.target.value })}
                        className="w-16 px-1 py-0.5 border border-slate-300 rounded font-mono text-right text-xs"
                        data-testid={`regularize-tech-${r.order_number}`}
                      />
                    </td>
                    <td className="p-2 text-right font-mono text-xs">${cost.toFixed(2)}</td>
                    <td className="p-2 text-right font-mono text-xs text-emerald-700">${profit.toFixed(2)}</td>
                    <td className="p-2 text-right font-mono text-xs">{comm == null ? "—" : `$${comm}`}</td>
                    <td className="p-2 text-xs">
                      {r.already_regularized ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <Check size={12} /> Regularizada
                        </span>
                      ) : (r.current_breakdown?.status === "OK") ? (
                        <span className="text-slate-500">OK</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle size={12} /> Pendiente
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">
            Motivo de la regularización (obligatorio)
          </label>
          <input
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Ej.: Setup inicial de catálogo de costos octubre 2026"
            className="input-base" data-testid="regularize-reason"
          />
        </div>
        <button
          type="button" onClick={applySelected}
          disabled={applying || selectedIds.length === 0 || (reason || "").trim().length < 3}
          className="btn-primary bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 min-w-[220px]"
          data-testid="regularize-apply"
        >
          {applying ? (
            <span className="inline-flex items-center gap-1"><Loader2 size={14} className="animate-spin" /> Aplicando…</span>
          ) : (
            `Recalcular ganancias (${selectedIds.length})`
          )}
        </button>
      </div>
    </div>
  );
}
